import { formatCurrency as canonicalFormatCurrency } from './financialFormat';

/**
 * Formatting Utilities
 * Common formatting functions for dates, currency, numbers, etc.
 */

/**
 * Format currency value
 */
export function formatCurrency(value: number | string | undefined, options?: {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}): string {
  // This shared helper historically defaulted to CENTS, and forensic callers
  // (e.g. AccountantMethodologyView's reconciliation figures) depend on the cent
  // precision. Preserve that default; honor an explicit maximumFractionDigits:0
  // request for whole-dollar. Null/zero/negative now follow the canonical law.
  return canonicalFormatCurrency(value, { cents: (options?.maximumFractionDigits ?? 2) !== 0 });
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatCompactNumber(value: number | undefined): string {
  return canonicalFormatCurrency(value);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number | undefined, decimals = 1): string {
  if (value === undefined || value === null) return '0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format date to locale string.
 * YYYY-MM-DD strings are parsed as local dates (midnight in local time) to
 * avoid the off-by-one that happens when a UTC midnight date is formatted in
 * a Western timezone (e.g. 2023-03-15 → Mar 14 in America/Chicago).
 */
export function formatDate(date: string | Date | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return 'N/A';

  let dateObj: Date;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split('-').map(Number);
    dateObj = new Date(y, m - 1, d);
  } else {
    dateObj = typeof date === 'string' ? new Date(date) : date;
  }

  if (isNaN(dateObj.getTime())) return 'Invalid Date';

  return dateObj.toLocaleDateString('en-US', options || {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date | undefined): string {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffYear > 0) return `${diffYear} year${diffYear > 1 ? 's' : ''} ago`;
  if (diffMonth > 0) return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
  if (diffWeek > 0) return `${diffWeek} week${diffWeek > 1 ? 's' : ''} ago`;
  if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  return 'Just now';
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes === 0) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Format phone number (US format)
 */
export function formatPhoneNumber(phone: string | undefined): string {
  if (!phone) return '';
  
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  return phone;
}

/**
 * Capitalize first letter of each word
 */
export function titleCase(text: string | undefined): string {
  if (!text) return '';

  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Humanize a machine label (snake_case / kebab-case enum) into Title Case words
 * for display. e.g. "needs_transaction_review" -> "Needs Transaction Review".
 * Use for user-facing status/type labels; keep the raw value for logic/keys.
 */
export function humanizeLabel(text: string | undefined | null): string {
  if (!text) return '';
  return text
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
